import registryAbi from '@/data/registryContractAbi.json';
import portalAbi from '@/data/portalContractAbi.json';
import commentAbi from '@/data/commentsContractAbi.json';
import { createPublicClient, Hex, http, fallback, webSocket } from 'viem';
import { CHAIN, RPC_URL, WS_RPC_ENDPOINT } from './constants';
import { captureException } from '@sentry/nextjs';
import { IDENTITY_MODULE_ABI } from '@/data/identity-module-abi';

export enum ContractType {
  Portal = 'Portal',
  Registry = 'Registry',
  Identity = 'Identity',
  Comment = 'Comment',
}

export enum PortalContractEvent {
  Mint = 'Mint',
  EditedFile = 'EditedFile',
  OwnershipTransferred = 'OwnershipTransferred',
  AddedFile = 'AddedFile',
  UpdatedKeyVerifiers = 'UpdatedKeyVerifiers',
  AddedCollaborator = 'AddedCollaborator',
  RemovedCollaborator = 'RemovedCollaborator',
  UpdatedPortalMetadata = 'UpdatedPortalMetadata',
  RemovedCollaboratorKeys = 'RemovedCollaboratorKeys',
  RegisteredCollaboratorKeys = 'RegisteredCollaboratorKeys',
  OwnershipTransferStarted = 'OwnershipTransferStarted',
}

export enum CommentContractEvent {
  CommentAdded = 'CommentAdded',
  CommentResolved = 'CommentResolved',
  CommentUnresolved = 'CommentUnresolved',
  CommentDeleted = 'CommentDeleted',
  ReplyAdded = 'ReplyAdded',
  ReplyDeleted = 'ReplyDeleted',
}

export enum ContractMethod {
  Mint = 'mint',
  AddCollaborator = 'addCollaborator',
  RegisterCollaboratorKeys = 'registerCollaboratorKeys',
  AddFile = 'addFile',
  EditFile = 'editFile',
  RemoveCollaborator = 'removeCollaborator',
  UpdateMetadata = 'updateMetadata',
  Files = 'files',
  UpdateKeyStoreContract = 'updateKeyStoreContract',
  TransferOwnership = 'transferOwnership',
  AcceptOwnership = 'acceptOwnership',
  AddComment = 'addComment',
  ResolveComment = 'resolveComment',
  UnresolveComment = 'unresolveComment',
  DeleteComment = 'deleteComment',
  GetComments = 'getComments',
  GetCommentsCount = 'getCommentCount',
  AddReply = 'addReply',
  DeleteReply = 'deleteReply',
  GetReplies = 'getReplies',
  GetReplyCount = 'getReplyCount',
}

export const CONTRACT_ABI_MAP = {
  [ContractType.Portal]: portalAbi,
  [ContractType.Registry]: registryAbi,
  [ContractType.Identity]: IDENTITY_MODULE_ABI,
  [ContractType.Comment]: commentAbi,
};

const transport = fallback(RPC_URL.map((url) => http(url)));

export const publicClient = createPublicClient({
  transport,
  chain: CHAIN,
  batch: {
    multicall: true,
  },
});

export const websocketClient = createPublicClient({
  transport: webSocket(WS_RPC_ENDPOINT),
  chain: CHAIN,
});

export const getContractFileData = async (
  contractAddress: string,
  fileId: number
) => {
  try {
    const [metadataIPFSHash, contentIPFSHash] =
      (await publicClient.readContract({
        address: contractAddress as Hex,
        abi: CONTRACT_ABI_MAP[ContractType.Portal],
        functionName: ContractMethod.Files,
        args: [fileId],
      })) as string[];
    if (!metadataIPFSHash || !contentIPFSHash)
      throw new Error('Failed to fetch file');

    return { metadataIPFSHash, contentIPFSHash };
  } catch (e) {
    captureException(e);
    throw new Error('Please try refreshing');
  }
};

export const getContractOwner = async (contractAddress: string) => {
  return publicClient.readContract({
    address: contractAddress as Hex,
    abi: CONTRACT_ABI_MAP[ContractType.Portal],
    functionName: 'owner',
  });
};
